/**
 * Model & Persona Wizard - Main wizard page with step navigation
 */

import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useWizardState } from '@/hooks/useWizardState';
import { useWizardPersistence } from '@/hooks/useWizardPersistence';
import { useUrlState } from '@/hooks/useUrlState';
import { toast } from '@/hooks/use-toast';
import { MainLayout } from '@/components/layout/MainLayout';
import { PageTransition } from '@/components/layout/PageTransition';
import { Breadcrumbs } from '@/components/layout/Breadcrumbs';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, ArrowRight, Save, FolderOpen, Home } from 'lucide-react';
import { EnvironmentStep } from '@/components/wizard/EnvironmentStep';
import { ModelSelectionStep } from '@/components/wizard/ModelSelectionStep';
import { SizingTcoPreviewStep } from '@/components/wizard/SizingTcoPreviewStep';
import { SoftwareBlueprintsStep } from '@/components/wizard/SoftwareBlueprintsStep';
import { LoanerRequestStep } from '@/components/wizard/LoanerRequestStep';
import { LoadScenarioDialog } from '@/components/wizard/LoadScenarioDialog';
import { ExportImportButtons } from '@/components/wizard/ExportImportButtons';

const STEP_TITLES = [
  'Configure Infrastructure & Personas',
  'Select Models',
  'Sizing & TCO Preview',
  'Software & Blueprints',
  'Request Loaner',
];

const STEP_DESCRIPTIONS = [
  'Define personas, constraints, and usage patterns',
  'Choose AI models based on your requirements',
  'Preview sizing estimates and total cost of ownership',
  'Download infrastructure blueprints and Docker containers',
  'Request demo hardware from Dell Demo Depot',
];

export default function WizardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { state, updateProgress, isLoading } = useWizardState();
  const { saving, saveScenario } = useWizardPersistence();
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);
  
  // Initialize URL state management
  useUrlState();
  
  const currentStepParam = parseInt(searchParams.get('step') || '1');
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3 | 4 | 5>(
    Math.min(Math.max(currentStepParam, 1), 5) as 1 | 2 | 3 | 4 | 5
  );

  useEffect(() => {
    setSearchParams({ step: currentStep.toString() });
  }, [currentStep, setSearchParams]);

  useEffect(() => {
    if (state.progress.currentStep !== currentStep) {
      updateProgress({ currentStep: Math.min(currentStep, 5) as 1 | 2 | 3 | 4 | 5 });
    }
  }, [currentStep, state.progress.currentStep, updateProgress]);

  const progress = ((currentStep - 1) / 4) * 100;

  const handleNext = () => {
    // Validate before proceeding
    if (!canProceed()) {
      if (currentStep === 1) {
        if (state.personas.length === 0) {
          toast({
            title: 'Personas Required',
            description: 'Please add at least one persona to continue.',
            variant: 'destructive',
          });
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
      return;
    }

    if (currentStep < 5) {
      // Mark current step as complete
      switch (currentStep) {
        case 1:
          updateProgress({ environmentComplete: true });
          break;
        case 2:
          updateProgress({ modelsComplete: true });
          break;
        case 3:
          updateProgress({ sizingPreviewComplete: true });
          break;
        case 4:
          updateProgress({ softwareComplete: true });
          break;
      }
      setCurrentStep((currentStep + 1) as 1 | 2 | 3 | 4 | 5);
    } else {
      // On final step, show completion message
      toast({
        title: 'Wizard Complete',
        description: 'You can now request loaner hardware',
      });
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((currentStep - 1) as 1 | 2 | 3 | 4 | 5);
    } else {
      navigate('/');
    }
  };

  const handleSave = async () => {
    await saveScenario();
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        // Require at least one persona
        return state.personas.length > 0;

      case 2:
        return state.modelSelections.selectedModelIds.length > 0;

      case 3:
        // Allow proceeding as long as at least one model is selected
        return state.modelSelections.selectedModelIds.length > 0;

      default:
        return true;
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="p-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="ml-3 text-muted-foreground">Loading wizard...</span>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <MainLayout showFooter={false}>
      <PageTransition>
      <div className="container mx-auto px-4 py-8 max-w-6xl">
      <Breadcrumbs />
      {/* Load Scenario Dialog */}
      <LoadScenarioDialog 
        open={loadDialogOpen} 
        onOpenChange={setLoadDialogOpen}
        onScenarioLoaded={() => {
          // Refresh to step 1 after loading
          setCurrentStep(1);
        }}
      />

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">Model & Persona Wizard</h1>
            <p className="text-muted-foreground">
              Configure your AI infrastructure in a few simple steps
            </p>
          </div>
        <div className="flex items-center gap-2">
          {currentStep === 1 && (
            <Button 
              variant="outline" 
              onClick={() => setLoadDialogOpen(true)} 
              className="gap-2"
            >
              <FolderOpen className="h-4 w-4" />
              Load Saved
            </Button>
          )}
          {currentStep < 4 && (
            <Button variant="outline" onClick={handleSave} disabled={saving} className="gap-2">
              <Save className="h-4 w-4" />
              {saving ? 'Saving...' : 'Save Progress'}
            </Button>
          )}
          {currentStep === 4 && <ExportImportButtons />}
          </div>
        </div>
      </div>

      {/* Progress Indicator */}
      <div className="mb-8">
        <div className="flex justify-between mb-2">
          {STEP_TITLES.map((title, index) => (
            <div
              key={index}
              className={`flex items-center ${
                index + 1 === currentStep ? 'text-primary font-semibold' : 'text-muted-foreground'
              }`}
            >
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center mr-2 ${
                  index + 1 === currentStep
                    ? 'bg-primary text-primary-foreground'
                    : index + 1 < currentStep
                    ? 'bg-green-500 text-white'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {index + 1}
              </div>
            </div>
          ))}
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      {/* Step Title and Description */}
      <div className="mb-6">
        <h2 className="text-2xl font-semibold mb-2">
          Step {currentStep}: {STEP_TITLES[currentStep - 1]}
        </h2>
        <p className="text-muted-foreground">
          {STEP_DESCRIPTIONS[currentStep - 1]}
        </p>
      </div>

      {/* Step Content */}
      <Card className="p-6 mb-6">
        {currentStep === 1 && <EnvironmentStep />}
        {currentStep === 2 && <ModelSelectionStep />}
        {currentStep === 3 && <SizingTcoPreviewStep />}
        {currentStep === 4 && <SoftwareBlueprintsStep />}
        {currentStep === 5 && <LoanerRequestStep />}
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={handleBack}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {currentStep === 1 ? 'Exit' : 'Back'}
        </Button>

        {currentStep < 5 && (
          <Button
            onClick={handleNext}
            disabled={!canProceed()}
          >
            Next
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        )}
      </div>
      </div>
      </PageTransition>
    </MainLayout>
  );
}
